/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import HelpIcon from '@mui/icons-material/Help';

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import LogoutButton from "components/LogoutButton";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";

// Soft UI Dashboard PRO React example components
import Breadcrumbs from "examples/Breadcrumbs";

import NotificationItem from "examples/Items/NotificationItem";
// import { useNavigate } from "react-router-dom";
import { useAuth0 } from '@auth0/auth0-react';


import { loadToken, getUser } from "api/user"
// Custom styles for DashboardCardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "./styles";

// Soft UI Dashboard PRO React context
import {
  useSoftUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Images
import team2 from "assets/images/team-2.jpg";
import logoSpotify from "assets/images/small-logos/logo-spotify.svg";

import { QueryClient, QueryClientProvider, useQuery, useMutation } from '@tanstack/react-query';
import { Grid } from "@mui/material";
import breakpoints from "assets/theme/base/breakpoints";

function DashboardCardNavbar({ navTitle, absolute = false, light = false, isMini = false }) {
  const { isLoading, user } = useAuth0();
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, deviceStyle } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const [previousState, setPreviousState] = useState(-1);

  const navigate = useNavigate();

  // console.log(useLocation())
  // console.log(route);

  loadToken(useAuth0());

  const userQuery = useQuery({
    queryKey: ['getUser'],
    queryFn: async () => {
      return await getUser();
    }
  });

  // console.log(user);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    // window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  // useEffect(() => {
  //   console.log(document.referrer)
  //   setPreviousState(document.referrer);
  // }, []);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const navigateToHelpPage = () => navigate('/help');
  const handleNavigateToPrevState = () => navigate(previousState);

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <LogoutButton>
        <SoftButton
          component={Link}
          href="https://www.creative-tim.com/product/soft-ui-dashboard-pro-react"
          target="_blank"
          rel="noreferrer"
          color="info"
          variant="gradient"
          fullWidth
        >
          Log out
        </SoftButton>
      </LogoutButton>
    </Menu>
  );

  const renderMobileStyleIcons = (
    <Grid container>
      <Grid item xxs={9} xs={10} md={9}>
        <SoftBox color="inherit" mb={{ xxs: 0, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" path={navTitle} title={navTitle[navTitle.length - 1]} route={route} light={light} />
          <Icon fontSize="medium" sx={navbarDesktopMenu} onClick={handleMiniSidenav}>
            {miniSidenav ? "menu_open" : "menu"}
          </Icon>
        </SoftBox>
      </Grid>
      <Grid item xxs={3} xs={2} md={3}>
        <SoftBox color={light ? "white" : "inherit"}>
          <Grid container>
            <Grid item xxs={6} sx={{ display: 'flex', flexDirection: 'row', justifyContent: "flex-end" }}>
              <IconButton
                sx={navbarIconButton}
                size="small"
                color="inherit"
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}>
                <SoftTypography
                  variant="button"
                  fontWeight="medium"
                  color={light ? "white" : "dark"}
                  textTransform="capitalize"
                  pt={0.35}
                >
                  {user.nickname}
                </SoftTypography>
                <Icon
                  sx={({ palette: { dark, white } }) => ({
                    color: light ? white.main : dark.main,
                  })}
                >
                  account_circle
                </Icon>
              </IconButton>
              {renderMenu()}
            </Grid>
            <Grid item xxs={6} sx={{ display: 'flex', flexDirection: 'row', justifyContent: "flex-end" }}>
              <IconButton
                size="small"
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon className={light ? "text-white" : "text-dark"}>
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
            </Grid>
            <Grid item xxs={6} sx={{ display: 'flex', flexDirection: 'row', justifyContent: "flex-end" }}>
              <IconButton
                size="small"
                color="inherit"
                sx={navbarIconButton}
                onClick={navigateToHelpPage}
              >
                <HelpIcon />
              </IconButton>
            </Grid>
            <Grid item xxs={6} sx={{ display: 'flex', flexDirection: 'row', justifyContent: "flex-end" }}>
              <IconButton
                size="small"
                color="inherit"
                sx={navbarIconButton}
                onClick={handleNavigateToPrevState}
              >
                <Icon>close</Icon>
              </IconButton>
            </Grid>
          </Grid>
        </SoftBox>
      </Grid>
    </Grid>
  )

  const renderDesktopStyleIcons = (
    <>
      <SoftBox color="inherit" mb={{ xxs: 0, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
        <Breadcrumbs icon="home" path={navTitle} title={navTitle[navTitle.length - 1]} route={route} light={light} />
        <Icon fontSize="medium" sx={navbarDesktopMenu} onClick={handleMiniSidenav}>
          {miniSidenav ? "menu_open" : "menu"}
        </Icon>
      </SoftBox>
      <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
        <SoftBox color={light ? "white" : "inherit"} sx={{ alignItems: "center" }}>
          <IconButton
            sx={navbarIconButton}
            size="small"
            color="inherit"
            aria-controls="notification-menu"
            aria-haspopup="true"
            variant="contained"
            onClick={handleOpenMenu}>
            <SoftTypography
              variant="button"
              fontWeight="medium"
              color={light ? "white" : "dark"}
              textTransform="capitalize"
              pt={0.35}
            >
              {user.nickname}
            </SoftTypography>
            <Icon
              sx={({ palette: { dark, white } }) => ({
                color: light ? white.main : dark.main,
              })}
            >
              account_circle
            </Icon>
          </IconButton>
          {renderMenu()}
          <IconButton
            size="small"
            color="inherit"
            sx={navbarMobileMenu}
            onClick={handleMiniSidenav}
          >
            <Icon className={light ? "text-white" : "text-dark"}>
              {miniSidenav ? "menu_open" : "menu"}
            </Icon>
          </IconButton>
          <IconButton
            size="small"
            color="inherit"
            sx={navbarIconButton}
            onClick={navigateToHelpPage}
          >
            <HelpIcon />
          </IconButton>
          <IconButton
            size="small"
            color="inherit"
            sx={navbarIconButton}
            onClick={handleNavigateToPrevState}
          >
            <Icon>close</Icon>
          </IconButton>
        </SoftBox>
      </SoftBox>
    </>
  )

  return (
    <AppBar position={absolute ? "absolute" : navbarType} color="inherit" sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}>
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        {window.innerWidth < breakpoints.values.md ? renderMobileStyleIcons : renderDesktopStyleIcons}
      </Toolbar>
    </AppBar>
  );
}

// Typechecking props for the DashboardCardNavbar
DashboardCardNavbar.propTypes = {
  navTitle: PropTypes.array,
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardCardNavbar;
